import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import AssetType from '../../components/Inputs/AssetType';
import Input from '../../common/Form/Input';
import Loader from '../../common/Loader';
import { actualValueForDropdown } from '../../utils/common';
import PavDynamicAssetField from './PavDynamicAssetField';
import PavOcrDetailsButton from './PavOcrDetailsButton';
import {
    PAV_ATTACHMENT_DRAG_TYPE,
    buildAssetData,
    formatOcrKey,
    getPavAssetFieldDisplayValue,
    getOcrAssetTypeOption,
    getOcrAssetTypeSearchValue,
    getOcrAssetTypeValue,
    hasPavAssetFieldValue,
    normalizeOcrKey,
} from './pavAssetUtils';

const CARD_INPUT_STYLE = { height: '2.5rem' };
const CARD_INPUT_CLASS = 'block w-full border-b-2 border-l-0 border-r-0 border-t-0 border-scogoddd bg-white px-0 text-font11 font-normal text-scogo21 placeholder:text-font10 placeholder:font-normal placeholder:text-inputfieldtext focus:border-scogoprimary focus:ring-0';
const OCR_INPUT_CLASS = 'block w-full border-b-2 border-l-0 border-r-0 border-t-0 border-emerald-300 bg-emerald-50/50 px-0 text-font11 font-medium text-scogo21 placeholder:text-font10 placeholder:font-normal placeholder:text-inputfieldtext focus:border-scogoprimary focus:ring-0';
const MISSING_INPUT_CLASS = 'block w-full border-b-2 border-l-0 border-r-0 border-t-0 border-amber-300 bg-white px-0 text-font11 font-normal text-scogo21 placeholder:text-font10 placeholder:font-normal placeholder:text-inputfieldtext focus:border-scogoprimary focus:ring-0';
const CARD_LABEL_CLASS = '!text-font10 !font-medium !text-slate-600';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_ROW_THUMBNAILS = 3;

const hasValue = (value) => value !== undefined && value !== null && String(value).trim() !== '';

const AssetThumbnails = ({ attachments, index, onPreview, onAddFiles, onUseOcrValue, isCompact, isFullScreen }) => {
    const fileInputRef = useRef(null);
    // Every image carries its own OCR badge, so the extra ones are revealed in
    // place instead of being collapsed behind a preview.
    const [showAllThumbnails, setShowAllThumbnails] = useState(false);
    const visibleAttachments = showAllThumbnails ? attachments : attachments.slice(0, MAX_ROW_THUMBNAILS);
    const hiddenCount = attachments.length - visibleAttachments.length;
    const thumbSizeClass = isFullScreen ? 'h-24 w-24' : isCompact ? 'h-12 w-12' : 'h-14 w-14';

    return (
        <div className='flex min-h-[3.5rem] flex-wrap items-center gap-2'>
            <input
                ref={fileInputRef}
                type='file'
                accept='image/*'
                multiple
                className='hidden'
                onChange={(event) => {
                    onAddFiles?.(event.target.files);
                    event.target.value = '';
                }}
            />

            {attachments.length === 0 ? (
                <button
                    type='button'
                    onClick={() => fileInputRef.current?.click()}
                    className={`group relative flex ${isFullScreen ? 'h-24' : 'h-13'} w-full items-center gap-2.5 rounded-lg border border-dashed border-red-300 bg-[#fff8f8] px-3 text-left transition-all hover:border-red-400 hover:bg-red-50`}
                    title={`Add images to Asset ${index + 1} - click anywhere to upload`}
                >
                    <span className='absolute -right-1 -top-1 flex h-2.5 w-2.5'>
                        <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75' />
                        <span className='relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500' />
                    </span>
                    <span className='flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-red-100 bg-white text-red-500 shadow-sm transition-transform group-hover:scale-105'>
                        <span className='material-icons-outlined text-font18'>add_photo_alternate</span>
                    </span>
                    <div className='min-w-0 flex-1 whitespace-nowrap'>
                        <span className='block truncate text-font11 font-semibold leading-tight text-scogo21'>
                            Asset {index + 1}
                        </span>
                        <span className='mt-0.5 flex items-center gap-1 text-font10 font-semibold leading-tight text-red-600'>
                            <span className='h-1.5 w-1.5 shrink-0 rounded-full bg-red-500' />
                            <span className='truncate'>Photo required</span>
                        </span>
                    </div>
                </button>
            ) : (
                <>
                    {visibleAttachments.map((attachment) => (
                        <div
                            key={attachment.id}
                            draggable
                            onDragStart={(event) => {
                                event.dataTransfer.setData(PAV_ATTACHMENT_DRAG_TYPE, attachment.id);
                                event.dataTransfer.effectAllowed = 'move';
                            }}
                            className={`relative ${thumbSizeClass} shrink-0 cursor-grab overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm active:cursor-grabbing`}
                            title={`${attachment.name} - drag onto another asset row to move it`}
                        >
                            <img src={attachment.preview_url || attachment.url} alt={attachment.name} className='h-full w-full object-cover' />
                            <button type='button' onClick={() => onPreview(attachment.id)} className='absolute inset-0 z-10 transition-colors hover:bg-slate-900/10' aria-label={`Preview ${attachment.name}`} />
                            <PavOcrDetailsButton
                                attachment={attachment}
                                className='absolute bottom-1 right-1'
                                size={isFullScreen ? 'lg' : 'sm'}
                                onUseValue={(field) => onUseOcrValue(field, attachment)}
                            />
                        </div>
                    ))}
                    {hiddenCount > 0 && (
                        <button type='button' onClick={() => setShowAllThumbnails(true)} className={`flex ${thumbSizeClass} shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-scogof8 text-font10 font-semibold text-scogoprimary hover:bg-scogolightblue`} title={`Show all ${attachments.length} images`}>
                            +{hiddenCount}
                        </button>
                    )}
                    {onAddFiles && (
                        <button
                            type='button'
                            onClick={() => fileInputRef.current?.click()}
                            className={`flex ${thumbSizeClass} shrink-0 flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 bg-white text-slate-500 transition-colors hover:border-scogoprimary hover:bg-scogolightblue hover:text-scogoprimary`}
                            title={`Add images to Asset ${index + 1} - they are scanned and fill this asset`}
                        >
                            <span className='material-icons-outlined text-font16'>add_photo_alternate</span>
                            <span className='text-font09 font-medium'>Add</span>
                        </button>
                    )}
                </>
            )}
        </div>
    );
};

const PavAssetCard = forwardRef(function PavAssetCard({ group, index, ticket, containerRef, assetTypes, initialValues, isCompact, isFullScreen, shouldFocus, canApplyToAll, onStatusChange, onRemoveGroup, onPreviewAttachment, onAddFiles, onApplyOwnerToAll, onMoveAttachment }, ref) {
    const dragDepthRef = useRef(0);
    const rowRef = useRef(null);
    const previousOcrValuesRef = useRef(new Map());
    // OCR readings are only a suggestion, so anything the user typed or picked
    // wins: a field is never re-synced once it has been edited by hand.
    const editedFieldsRef = useRef(new Set());
    const [isDropTarget, setIsDropTarget] = useState(false);
    const [isMinimized, setIsMinimized] = useState(Boolean(isCompact));
    const newLoggedUserData = useSelector((state) => state.auth.newLoggedUserData);
    const { resolveAssetType, requestAssetType, isTermSettled } = assetTypes;
    const methods = useForm({
        mode: 'onChange',
        defaultValues: { ...(initialValues || {}) },
    });
    const values = methods.watch();
    const selectedAssetType = values.asset_type_id;

    useEffect(() => {
        const subscription = methods.watch((_values, { name, type }) => {
            if (name && type === 'change') editedFieldsRef.current.add(name);
        });
        return () => subscription.unsubscribe();
    }, [methods]);

    const ocrAssetTypeValue = (group.canonicalFields.get('asset_type_id') || group.canonicalFields.get('asset_type'))?.value;
    const ocrAssetTypeSourceAttachment = useMemo(() => (
        group.attachments.find((attachment) => attachment.status === 'complete' && getOcrAssetTypeValue(attachment.ocr_data))
    ), [group.attachments]);
    const directOcrAssetType = useMemo(
        () => getOcrAssetTypeOption(ocrAssetTypeSourceAttachment?.ocr_data),
        [ocrAssetTypeSourceAttachment]
    );
    const matchedOcrAssetType = useMemo(
        () => resolveAssetType(ocrAssetTypeValue) || directOcrAssetType,
        [resolveAssetType, directOcrAssetType, ocrAssetTypeValue]
    );
    const ocrAssetTypeSearchValue = getOcrAssetTypeSearchValue(ocrAssetTypeSourceAttachment?.ocr_data) || ocrAssetTypeValue;

    useEffect(() => {
        if (!ocrAssetTypeValue || matchedOcrAssetType) return;
        requestAssetType(ocrAssetTypeSearchValue);
    }, [ocrAssetTypeValue, ocrAssetTypeSearchValue, matchedOcrAssetType, requestAssetType]);

    useEffect(() => {
        if (!matchedOcrAssetType || editedFieldsRef.current.has('asset_type_id')) return;
        const matchedAssetTypeId = actualValueForDropdown(matchedOcrAssetType);
        if (actualValueForDropdown(methods.getValues('asset_type_id')) !== matchedAssetTypeId) {
            methods.setValue('asset_type_id', matchedOcrAssetType, { shouldDirty: false, shouldValidate: true });
        }
    }, [matchedOcrAssetType, methods]);

    const effectiveAssetType = selectedAssetType || matchedOcrAssetType;
    const assetFields = useMemo(() => (effectiveAssetType?.fields || [])
        .filter((field) => !['asset_type', 'asset_type_id'].includes(normalizeOcrKey(field.key)))
        .map((field) => ({
            ...field,
            ocrField: group.canonicalFields.get(normalizeOcrKey(field.key)),
        })), [effectiveAssetType, group.canonicalFields]);
    const ocrFilledFieldCount = assetFields.filter(({ ocrField }) => hasValue(ocrField?.value)).length;
    const serialNumberField = assetFields.find((field) => normalizeOcrKey(field.key) === 'serial_number');
    const serialNumber = String(
        (serialNumberField ? values[serialNumberField.key] : group.canonicalFields.get('serial_number')?.value) || ''
    ).trim();

    // Keep the form in sync with the latest OCR result. Deleting or replacing an
    // image removes the stale value it supplied. Fields the user has already
    // edited are left alone in both directions.
    useEffect(() => {
        const nextOcrValues = new Map();
        assetFields.forEach(({ key, ocrField }) => {
            if (!key || !hasValue(ocrField?.value)) return;
            nextOcrValues.set(key, ocrField.value);
            if (editedFieldsRef.current.has(key)) return;
            if (methods.getValues(key) !== ocrField.value) {
                methods.setValue(key, ocrField.value, { shouldDirty: false, shouldValidate: true });
            }
        });
        previousOcrValuesRef.current.forEach((previousValue, key) => {
            if (nextOcrValues.has(key) || editedFieldsRef.current.has(key)) return;
            if (String(methods.getValues(key) ?? '') === String(previousValue)) {
                methods.setValue(key, '', { shouldDirty: false, shouldValidate: true });
            }
        });
        previousOcrValuesRef.current = nextOcrValues;
    }, [assetFields, methods]);

    // Puts one image's reading into this card, overriding whatever the merged
    // OCR result picked. Returns the label of the field it filled, or null when
    // the selected asset type has nowhere to put the value.
    const applyOcrField = useCallback((ocrField, sourceAttachment) => {
        const normalizedKey = normalizeOcrKey(ocrField?.key);
        if (!normalizedKey || !hasValue(ocrField?.value)) return null;

        if (['asset_type', 'asset_type_id'].includes(normalizedKey)) {
            const assetTypeOption = resolveAssetType(ocrField.value) || getOcrAssetTypeOption(sourceAttachment?.ocr_data);
            if (!assetTypeOption) return null;
            editedFieldsRef.current.add('asset_type_id');
            methods.setValue('asset_type_id', assetTypeOption, { shouldDirty: true, shouldValidate: true });
            return 'Asset Type';
        }

        const targetField = assetFields.find((field) => normalizeOcrKey(field.key) === normalizedKey);
        if (!targetField) return null;
        editedFieldsRef.current.add(targetField.key);
        methods.setValue(targetField.key, ocrField.value, { shouldDirty: true, shouldValidate: true });
        return targetField.label || formatOcrKey(targetField.key);
    }, [assetFields, methods, resolveAssetType]);

    const isResolvingOcrAssetType = Boolean(
        ocrAssetTypeValue
        && !matchedOcrAssetType
        && !isTermSettled(ocrAssetTypeSearchValue)
    );
    const ownerName = String(values.owner_name || '').trim();
    const ownerEmail = String(values.contact_email || '').trim();
    // Owner details are optional; an entered email is still validated by the
    // form's email pattern, which react-hook-form skips while the field is empty.
    const hasOwnerDetails = Boolean(ownerName || ownerEmail);
    const hasAttachments = group.attachments.length > 0;
    const isValid = Boolean(
        hasAttachments
        && methods.formState.isValid
        && actualValueForDropdown(selectedAssetType)
        && (selectedAssetType?.fields || []).every((field) => !field.is_required || hasPavAssetFieldValue(field, values[field.key]))
    );
    const hasAssetData = Boolean(
        actualValueForDropdown(selectedAssetType)
        || assetFields.some(({ key }) => hasValue(values[key]))
        || group.canonicalFields.size > 0
    );
    const assetTypeLabel = effectiveAssetType?.label || 'Unidentified asset';

    const summarySignature = JSON.stringify({
        assetTypeLabel,
        isValid,
        hasAttachments,
        attachmentCount: group.attachments.length,
        fields: assetFields
            .map((field) => ({ key: field.key, label: field.label, value: String(getPavAssetFieldDisplayValue(field, values[field.key])).trim() }))
            .filter((field) => field.value),
        owner: { name: ownerName, email: ownerEmail },
    });

    useEffect(() => {
        onStatusChange(group.id, JSON.parse(summarySignature));
    }, [group.id, summarySignature, onStatusChange]);

    useEffect(() => {
        if (!shouldFocus) return;
        const frame = window.requestAnimationFrame(() => {
            rowRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
            const firstEditableInput = rowRef.current?.querySelector('input:not([readonly]):not([disabled])');
            if (!isMinimized && firstEditableInput) firstEditableInput.focus();
            else rowRef.current?.focus();
        });
        return () => window.cancelAnimationFrame(frame);
    }, [shouldFocus, isMinimized]);

    useImperativeHandle(ref, () => ({
        validate: async () => {
            const formValid = await methods.trigger();
            const currentValues = methods.getValues();
            const currentAssetType = currentValues.asset_type_id;
            const currentOwnerEmail = String(currentValues.contact_email || '').trim();
            const hasCardAttachments = group.attachments.length > 0;
            const requiredValuesComplete = Boolean(
                hasCardAttachments
                && actualValueForDropdown(currentAssetType)
                && (!currentOwnerEmail || EMAIL_PATTERN.test(currentOwnerEmail))
                && (currentAssetType?.fields || []).every((field) => !field.is_required || hasPavAssetFieldValue(field, currentValues[field.key]))
            );
            return {
                valid: formValid && requiredValuesComplete,
                hasAttachments: hasCardAttachments,
                data: buildAssetData(currentValues, group.attachments),
                assetTypeLabel: currentAssetType?.label || 'Asset',
            };
        },
        expand: () => {
            setIsMinimized(false);
            window.requestAnimationFrame(() => {
                rowRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
                rowRef.current?.querySelector('input:not([readonly]):not([disabled])')?.focus();
            });
        },
        getFormValues: () => methods.getValues(),
        applyOcrField,
        getOwner: () => ({ owner_name: methods.getValues('owner_name'), contact_email: methods.getValues('contact_email') }),
        setOwner: (owner) => {
            methods.setValue('owner_name', owner.owner_name || '', { shouldValidate: true });
            methods.setValue('contact_email', owner.contact_email || '', { shouldValidate: true });
        },
    }));

    const setRowNode = (node) => {
        rowRef.current = node;
        if (typeof containerRef === 'function') containerRef(node);
        else if (containerRef) containerRef.current = node;
    };

    const isAttachmentDrag = (event) => Array.from(event.dataTransfer?.types || []).includes(PAV_ATTACHMENT_DRAG_TYPE);
    const dropProps = onMoveAttachment ? {
        onDragEnter: (event) => {
            if (!isAttachmentDrag(event)) return;
            event.preventDefault();
            dragDepthRef.current += 1;
            setIsDropTarget(true);
        },
        onDragOver: (event) => {
            if (!isAttachmentDrag(event)) return;
            event.preventDefault();
            event.dataTransfer.dropEffect = 'move';
        },
        onDragLeave: (event) => {
            if (!isAttachmentDrag(event)) return;
            dragDepthRef.current = Math.max(dragDepthRef.current - 1, 0);
            if (dragDepthRef.current === 0) setIsDropTarget(false);
        },
        onDrop: (event) => {
            if (!isAttachmentDrag(event)) return;
            event.preventDefault();
            dragDepthRef.current = 0;
            setIsDropTarget(false);
            const attachmentId = event.dataTransfer.getData(PAV_ATTACHMENT_DRAG_TYPE);
            if (attachmentId) onMoveAttachment(attachmentId, group.id);
        },
    } : {};

    const fieldGridClass = isCompact ? 'grid-cols-2' : 'grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';
    const borderClass = isDropTarget
        ? 'border-scogobgsky bg-scogolightblue ring-2 ring-blue-200'
        : !hasAttachments
            ? 'border-red-300 bg-white ring-1 ring-red-100'
            : !hasAssetData
                ? 'border-dashed border-amber-400 bg-amber-50/30 ring-1 ring-amber-100'
                : !isValid
                    ? 'border-amber-300 bg-white'
                    : 'border-slate-200 bg-white';

    return (
        <FormProvider {...methods}>
            <div ref={setRowNode} tabIndex={-1} {...dropProps} className={`relative rounded-lg border outline-none transition-colors focus:ring-2 focus:ring-amber-200 ${isCompact && isMinimized ? 'p-0' : 'p-3'} ${borderClass}`}>
                {isCompact && isMinimized && (
                    <div className='flex min-h-[4.2rem] items-center gap-2 px-3 py-2'>
                        <button type='button' onClick={() => setIsMinimized(false)} aria-expanded='false' className='flex min-w-0 flex-1 items-center gap-2 text-left' title='Maximize asset details'>
                            <span className='relative flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-scogolightblue text-scogoprimary'>
                                <span className='material-icons-outlined text-font14'>inventory_2</span>
                                {!hasAttachments && (
                                    <span className='absolute -right-1 -top-1 flex h-2.5 w-2.5'>
                                        <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75' />
                                        <span className='relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white' />
                                    </span>
                                )}
                            </span>
                            <span className='grid min-w-0 flex-1 grid-cols-2 gap-3'>
                                <span className='min-w-0'>
                                    <span className='block text-font09 font-medium uppercase tracking-wide text-slate-500'>Asset Type</span>
                                    <span className='mt-0.5 block truncate text-font11 font-semibold text-scogo21' title={assetTypeLabel}>{isResolvingOcrAssetType ? 'Detecting...' : assetTypeLabel}</span>
                                </span>
                                <span className='min-w-0'>
                                    <span className='block text-font09 font-medium uppercase tracking-wide text-slate-500'>Serial Number</span>
                                    <span className='mt-0.5 block truncate text-font11 font-medium text-scogo21' title={serialNumber || 'Not available'}>{serialNumber || 'Not available'}</span>
                                </span>
                            </span>
                            <span className='material-icons shrink-0 text-font16 text-scogoprimary'>expand_more</span>
                        </button>
                        {onRemoveGroup && (
                            <button type='button' onClick={onRemoveGroup} className='flex h-7 w-7 shrink-0 items-center justify-center rounded text-slate-500 transition-colors hover:bg-red-50 hover:text-red-600' title={group.attachments.length > 0 ? 'Remove this asset and its images' : 'Remove this asset'}>
                                <span className='material-icons text-font14'>close</span>
                            </button>
                        )}
                    </div>
                )}

                <div className={isCompact && isMinimized ? 'hidden' : 'block'}>
                    <div className='absolute right-1.5 top-1.5 z-10 flex items-center gap-0.5'>
                        {isCompact && (
                            <button type='button' onClick={() => setIsMinimized(true)} aria-expanded='true' className='flex h-7 w-7 shrink-0 items-center justify-center rounded text-slate-500 transition-colors hover:bg-scogolightblue hover:text-scogoprimary' title='Minimize asset details'>
                                <span className='material-icons text-font16'>expand_less</span>
                            </button>
                        )}
                        {onRemoveGroup && (
                            <button type='button' onClick={onRemoveGroup} className='flex h-7 w-7 shrink-0 items-center justify-center rounded text-slate-500 transition-colors hover:bg-red-50 hover:text-red-600' title={group.attachments.length > 0 ? 'Remove this asset and its images' : 'Remove this asset'}>
                                <span className='material-icons text-font14'>close</span>
                            </button>
                        )}
                    </div>

                    <div className={`flex gap-3 ${isCompact ? 'flex-col pr-14' : 'items-start pr-6'}`}>
                        <div className={`shrink-0 ${isCompact ? 'w-full' : isFullScreen ? 'w-[19.5rem]' : 'w-[14rem]'}`}>
                            <AssetThumbnails attachments={group.attachments} index={index} onPreview={onPreviewAttachment} onAddFiles={onAddFiles} onUseOcrValue={applyOcrField} isCompact={isCompact} isFullScreen={isFullScreen} />
                        </div>

                        <div className='min-w-0 flex-1'>
                            {isResolvingOcrAssetType && (
                                <div className='mb-1 flex items-center gap-2 text-font10 text-slate-600'>
                                    <Loader size='12' thickness='2' color='#12355F' />
                                    Matching the OCR asset type...
                                </div>
                            )}
                            {ocrAssetTypeValue && !matchedOcrAssetType && !isResolvingOcrAssetType && (
                                <p className='mb-1.5 flex items-start gap-1 rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-font10 font-normal leading-4 text-amber-800'>
                                    <span className='material-icons-outlined mt-px shrink-0 text-font12'>info</span>
                                    <span>Detected "<strong className='font-semibold'>{String(ocrAssetTypeValue)}</strong>". Select the matching asset type.</span>
                                </p>
                            )}

                            {ocrFilledFieldCount > 0 && (
                                <p className='mb-1.5 flex items-start gap-1 text-font10 font-normal leading-4 text-slate-500'>
                                    <span className='material-icons-outlined mt-px shrink-0 text-font12 text-emerald-600'>auto_awesome</span>
                                    <span>{ocrFilledFieldCount} field{ocrFilledFieldCount === 1 ? '' : 's'} prefilled from the images. Every field is editable - correct anything the scan read wrong.</span>
                                </p>
                            )}

                            <div className={`grid gap-x-3 ${fieldGridClass}`}>
                                <AssetType
                                    label={ocrAssetTypeValue && !matchedOcrAssetType ? 'Confirm Asset Type' : 'Asset Type'}
                                    name='asset_type_id'
                                    org_id={ticket?.org_id || newLoggedUserData?.org?.id}
                                    required
                                    labelClassName={CARD_LABEL_CLASS}
                                    clearOnUnmount={false}
                                    reuseLoadedOptions
                                />
                                {assetFields.map((field) => {
                                    const isOcrFilled = hasValue(field.ocrField?.value);
                                    const isMissing = field.is_required && !hasPavAssetFieldValue(field, values[field.key]);
                                    return (
                                        <PavDynamicAssetField
                                            key={field.key}
                                            field={field}
                                            inputStyle={CARD_INPUT_STYLE}
                                            inputClassName={isOcrFilled ? OCR_INPUT_CLASS : isMissing ? MISSING_INPUT_CLASS : CARD_INPUT_CLASS}
                                            labelClassName={CARD_LABEL_CLASS}
                                            disableResponsiveFontSize
                                            isPavMilestoneAsset
                                        />
                                    );
                                })}
                                <Input label='Owner Name' name='owner_name' placeholder='Enter owner name (optional)' inputStyle={CARD_INPUT_STYLE} inputClassName={CARD_INPUT_CLASS} labelClassName={CARD_LABEL_CLASS} disableResponsiveFontSize />
                                <Input label='Owner Email' name='contact_email' type='email' placeholder='Enter owner email (optional)' pattern={EMAIL_PATTERN} inputStyle={CARD_INPUT_STYLE} inputClassName={CARD_INPUT_CLASS} labelClassName={CARD_LABEL_CLASS} disableResponsiveFontSize />
                            </div>

                            {canApplyToAll && hasOwnerDetails && (
                                <div className='flex justify-end'>
                                    <button type='button' onClick={() => onApplyOwnerToAll?.(group.id, { owner_name: ownerName, contact_email: ownerEmail })} className='rounded px-1 py-0.5 text-font10 font-medium text-slate-600 transition-colors hover:text-scogoprimary'>
                                        Apply owner to all assets
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </FormProvider>
    );
});

export default PavAssetCard;
